import { DateTime } from 'luxon';
import getNumberOfDays from './getNumberOfDays';
import relativeDateRangeTypeCheckers from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/FiscalPeriodsProvider/relativeDateRangeTypeCheckers';
import getFiscalComparison from './getFiscalComparison';
import getAdvancedFiscalComparison from './getAdvancedFiscalComparison';

const getCalendarComparison = (
  d: DateRangeInput,
  comparison: PersistedComparisonType,
  interval: FleetOps.Interval | ExtendedInterval | undefined,
  relativeDateRange?: RelativeDateRange,
  advancedRelativeDateRange?: AdvancedRelativeDateRange,
  allFiscals?: FixedPeriods.AllFiscals,
): DateRangeInput => {
  const isHourly = interval === 'hour';
  const baseDateRange = (() => {
    const s = d.startDate ? DateTime.fromISO(d.startDate) : DateTime.local();
    const e = d.endDate
      ? isHourly
        ? DateTime.fromISO(d.endDate).plus({
            hours: 23,
            minutes: 59,
            seconds: 59,
          })
        : DateTime.fromISO(d.endDate)
      : undefined;

    if (relativeDateRange) {
      if (
        interval &&
        relativeDateRangeTypeCheckers.isFiscalInterval(interval)
      ) {
        return getFiscalComparison(
          s,
          e,
          comparison,
          interval,
          relativeDateRange,
          allFiscals,
        );
      }
      if (comparison.compareType === 'previous') {
        if (
          comparison.compareByInterval &&
          comparison.compareByInterval !== 'day'
        ) {
          const offset = { [comparison.compareByInterval]: 1 };
          if (e) {
            return {
              startDate: s.minus(offset),
              endDate: e.minus(offset),
            };
          } else {
            return {
              startDate: s.minus(offset),
            };
          }
        }
        const isSingleInterval =
          relativeDateRange.n === 1 || relativeDateRange.type === 'current';
        // Special cases:
        // Month
        if (relativeDateRange.interval === 'month' && isSingleInterval) {
          if (e) {
            // Luxon preserves day-of-month when subtracting, so April 30
            // minus 1 month is March 30 — but we want the full previous
            // month (March 31). Snap to end-of-month when e is a month-end.
            const isMonthEnd = e.day === e.daysInMonth;
            const shifted = e.minus({ month: 1 });
            return {
              startDate: s.minus({ month: 1 }),
              endDate: isMonthEnd ? shifted.endOf('month') : shifted,
            };
          } else {
            return {
              startDate: s.minus({ month: 1 }),
            };
          }
        }
        // Week
        if (relativeDateRange.interval === 'week' && isSingleInterval) {
          if (e) {
            return {
              startDate: s.minus({ week: 1 }),
              endDate: e.minus({ week: 1 }),
            };
          } else {
            return {
              startDate: s.minus({ week: 1 }),
            };
          }
        }
        // Quarter
        if (relativeDateRange.interval === 'quarter') {
          const n = relativeDateRange.n ? relativeDateRange.n : 1;
          if (e) {
            // Same end-of-period issue as month: Q2 ends June 30, minus
            // 1 quarter is March 30 via Luxon — but Q1 ends March 31.
            const isQuarterEnd =
              e.day === e.daysInMonth && [3, 6, 9, 12].includes(e.month);
            const shifted = e.minus({ quarter: n });
            return {
              startDate: s.minus({ quarter: n }),
              endDate: isQuarterEnd ? shifted.endOf('quarter') : shifted,
            };
          } else {
            return {
              startDate: s.minus({ quarter: 1 }),
            };
          }
        }

        // Year
        if (relativeDateRange.interval === 'year' && isSingleInterval) {
          if (e) {
            return {
              startDate: s.minus({ year: 1 }),
              endDate: e.minus({ year: 1 }),
            };
          } else {
            return {
              startDate: s.minus({ year: 1 }),
            };
          }
        }

        const diffDays = getNumberOfDays(d);
        if (e) {
          return {
            startDate: s.minus({ days: diffDays + 1 }),
            endDate: e.minus({ days: diffDays + 1 }),
          };
        } else {
          return {
            startDate: s.minus({ days: diffDays + 1 }),
          };
        }
      }
    }
    let fiscalStart: DateTime | undefined = undefined;
    let fiscalEnd: DateTime | undefined = undefined;
    if (
      advancedRelativeDateRange &&
      (relativeDateRangeTypeCheckers.isFiscalInterval(
        advancedRelativeDateRange.from.interval,
      ) ||
        relativeDateRangeTypeCheckers.isFiscalInterval(
          advancedRelativeDateRange?.to.interval,
        ))
    ) {
      const advancedFiscalComparison = getAdvancedFiscalComparison(
        s,
        e,
        advancedRelativeDateRange,
        comparison,
        allFiscals,
      );

      fiscalStart = advancedFiscalComparison.startDate;
      fiscalEnd = advancedFiscalComparison.endDate;
    }

    if (comparison.compareType === 'previous') {
      if (
        comparison.compareByInterval &&
        comparison.compareByInterval !== 'day'
      ) {
        const offset = { [comparison.compareByInterval]: 1 };
        if (e) {
          return {
            startDate: fiscalStart || s.minus(offset),
            endDate: fiscalEnd || e.minus(offset),
          };
        } else {
          return {
            startDate: fiscalStart || s.minus(offset),
          };
        }
      }
      const diffDays = getNumberOfDays(d);
      if (e) {
        return {
          startDate: fiscalStart || s.minus({ days: diffDays + 1 }),
          endDate: fiscalEnd || e.minus({ days: diffDays + 1 }),
        };
      } else {
        return {
          startDate: fiscalStart || s.minus({ days: diffDays + 1 }),
        };
      }
    } else {
      const isWeeklyComparison =
        advancedRelativeDateRange?.from?.interval === 'week' &&
        advancedRelativeDateRange.to.interval === 'week';

      let comparisonOffset = isWeeklyComparison ? { weeks: 52 } : { year: 1 };
      if (e) {
        return {
          startDate: fiscalStart || s.minus(comparisonOffset),
          endDate: fiscalEnd || e.minus(comparisonOffset),
        };
      } else {
        return {
          startDate: fiscalStart || s.minus(comparisonOffset),
        };
      }
    }
  })();

  if (!isHourly) {
    return {
      startDate: baseDateRange.startDate?.toISODate(),
      endDate: baseDateRange.endDate
        ? baseDateRange.endDate.toISODate()
        : undefined,
    };
  }

  return {
    startDate: baseDateRange.startDate?.toISO(),
    endDate: baseDateRange.endDate ? baseDateRange.endDate.toISO() : undefined,
  };
};

export default getCalendarComparison;
